import React from 'react';
import { type Moment, type MomentInput } from 'moment';
import moment from 'moment-timezone';
import { addValidationRule, withFormsy } from 'formsy-react';
import { useIntl } from 'react-intl';
import { IconButton, styled } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';

import BaseTextField, { type FieldProps, type BaseTextFieldProps } from './components/BaseTextField';
import withFormsyValidations from './components/WithFormsyValidations';
import useSettings from '../hooks/useSettings';
import { parseDate } from '../Utils';
import Icon from '../Icon';

const globalMinDateTime = '1753-01-01T00:00:00';

addValidationRule<string | null>('minDateTime', (_model, newValue, minValue) => {
	const parsedCurrentDateTime = parseDate(newValue);
	const parsedMinDateTime = parseDate(minValue);

	if (parsedCurrentDateTime === null || parsedMinDateTime === null) return true;
	return parsedCurrentDateTime?.isSameOrAfter(parsedMinDateTime);
});

addValidationRule<string | null>('maxDateTime', (_model, newValue, maxValue) => {
	const parsedCurrentDateTime = parseDate(newValue);
	const parsedMaxDateTime = parseDate(maxValue);

	if (parsedCurrentDateTime === null || parsedMaxDateTime === null) return true;
	return parsedCurrentDateTime?.isSameOrBefore(parsedMaxDateTime);
});

type DateTimeFieldProps = Omit<FieldProps<string, Moment>, 'endAdornment'> & {
	shouldDisableDate?: (day: Moment) => boolean;
};

const DateTextField = styled(BaseTextField)(({ isEdit, theme }) => ({
	'&.MuiTextField-root > .MuiInputBase-root': {
		paddingRight: isEdit ? 4 : 12
	}
}));

/** The date time field, used to specify date and time */
function DateTimeField(props: DateTimeFieldProps) {
	const { sysSettings } = useSettings();
	const intl = useIntl();
	const hasLogs = props.logProps?.hasLogs(props.name);

	const ref = React.useRef<HTMLDivElement>(null);

	const minutesStep = Number.parseInt(sysSettings.min_time_interval, 10) || 15;

	const [dialogOpen, setDialogOpen] = React.useState(false);
	const switchDialogOpen = () => setDialogOpen(state => !state);
	const internalValue = React.useRef(parseDate(props.value));

	function fieldParser(fieldValue: MomentInput) {
		const parsedValue = parseDate(fieldValue);
		if (parsedValue !== null)
			return intl.formatDate(parsedValue?.toDate(), {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit'
			});
		return null;
	}

	function handleBlur() {
		const newValue = internalValue && internalValue.current?.isValid() ? internalValue.current?.utc() : undefined;
		if (props.beforeChange) props.beforeChange(newValue);
		props.sendChange(props.name, newValue?.toISOString() || null);
		if (props.afterChange) props.afterChange(newValue);
	}

	function handleChange(newValue: Moment | null) {
		internalValue.current = newValue ?? undefined;
		props.setValue(newValue?.toISOString() || null);
	}

	function handleDialogClose() {
		handleBlur();
		setDialogOpen(false);
	}

	const { minTime, maxTime, minDate, maxDate } = props.validations || {};

	return (
		<DateTimePicker
			ref={ref}
			className={props.className}
			name={props.name}
			label={props.label}
			format="DD/MM/YYYY HH:mm"
			formatDensity="dense"
			minTime={minTime ? minTime : undefined}
			maxTime={maxTime ? maxTime : undefined}
			minDate={minDate ? minDate : undefined}
			maxDate={maxDate ? maxDate : undefined}
			timeSteps={{ minutes: minutesStep || 5 }}
			value={props.value ? moment.utc(props.value) : null}
			slots={{ textField: DateTextField }}
			slotProps={{
				textField: {
					name: props.name,
					isEdit: props.isEdit,
					startAdornment: props.startAdornment,
					InputProps: {
						endAdornment: props.isEdit ? (
							<IconButton size="small" onClick={switchDialogOpen}>
								<Icon icon="calendar" />
							</IconButton>
						) : (
							<></>
						)
					},
					required: props.required,
					errorMessage: props.errorMessage,
					isFormSubmitted: props.isFormSubmitted,
					isPristine: props.isPristine,
					isValid: props.isValid,
					logProps: props.logProps,
					fieldParser,
					onBlur: handleBlur
				} as BaseTextFieldProps<string>,
				popper: {
					anchorEl: ref?.current
				}
			}}
			timezone={moment.tz.guess()}
			readOnly={hasLogs}
			disabled={!props.isEdit && !hasLogs}
			onChange={handleChange}
			onAccept={handleBlur}
			open={dialogOpen}
			onClose={handleDialogClose}
			ampm={false}
			displayWeekNumber
			desktopModeMediaQuery=""
			enableAccessibleFieldDOMStructure={false}
			skipDisabled
			shouldDisableDate={props.shouldDisableDate}
		/>
	);
}

export default React.memo(
	withFormsyValidations(withFormsy(DateTimeField), {
		validations: { minDateTime: globalMinDateTime },
		validationErrors: ({ t, intl }) => ({
			minDateTime: intl.formatMessage(
				{ id: 'dateTimeField.minDate', defaultMessage: t('ERROR_MIN_DATE') },
				{ value: intl.formatDate(parseDate(globalMinDateTime)?.toDate()) }
			)
		})
	})
);
